import { defineStore } from "pinia";
import { useStorage } from "@vueuse/core";

import { ResourceType } from "@/enums/resourceType.enum";

export const DEFAULT_PENDING_TTL_MS = 30_000;

export enum PendingMutationType {
  CREATE = "create",
  DELETE = "delete",
}

export type OptimisticRecord = {
  mutationType: PendingMutationType;
  data: Record<string, any>;
  createdAt: number;
  expiresAt: number;
};

type RecordWithId = { id: string; [key: string]: any };

type OptimisticRecordsByResource = {
  [K in ResourceType]: Record<string, OptimisticRecord>;
};

const emptyState = (): OptimisticRecordsByResource => ({
  [ResourceType.BROKERAGE]: {},
  [ResourceType.QUOTE]: {},
  [ResourceType.ROLE]: {},
});

export const useOptimisticRecordStore = defineStore("optimisticRecord", {
  state: () => {
    return {
      records: useStorage<OptimisticRecordsByResource>(
        "optimisticRecords",
        emptyState(),
        localStorage,
        {
          mergeDefaults: true,
        },
      ),
    };
  },

  actions: {
    registerCreate(resource: ResourceType, data: RecordWithId, ttlMs: number = DEFAULT_PENDING_TTL_MS): void {
      this.sweepExpired();

      if (data.id) {
        const now: number = Date.now();
        this.records[resource][data.id] = {
          mutationType: PendingMutationType.CREATE,
          data,
          createdAt: now,
          expiresAt: now + ttlMs,
        };
      }
    },

    registerDelete(resource: ResourceType, id: string, ttlMs: number = DEFAULT_PENDING_TTL_MS): void {
      this.sweepExpired();

      const now: number = Date.now();
      this.records[resource][id] = {
        mutationType: PendingMutationType.DELETE,
        data: { id },
        createdAt: now,
        expiresAt: now + ttlMs,
      };
    },

    clearResource(resource: ResourceType): void {
      this.records[resource] = {};
    },

    clearAll(): void {
      this.records = emptyState();
    },

    sweepExpired(): void {
      const now: number = Date.now();
      for (const resource of Object.values(ResourceType)) {
        const bucket = this.records[resource] ?? {};

        for (const id of Object.keys(bucket)) {
          if (bucket[id].expiresAt < now) {
            delete bucket[id];
          }
        }
      }
    },
  },

  getters: {
    reconcile: (state) => {
      return <T extends RecordWithId>(resource: ResourceType, results: T[]): T[] => {
        const bucket = state.records[resource] ?? {};
        const now: number = Date.now();

        const activeEntries = Object.entries(bucket).filter(
          ([, record]): boolean => record.expiresAt >= now,
        );

        if (activeEntries.length === 0) {
          return results;
        }

        const resultIds = new Set(results.map((item: T): any => item.id));
        const overlay: T[] = [];
        const deletedIds = new Set<string>();

        activeEntries.forEach(([id, record]: [string, OptimisticRecord]): void => {
          if (record.mutationType === PendingMutationType.CREATE) {
            if (!resultIds.has(id)) {
              overlay.push(record.data as T);
            }
          } else if (resultIds.has(id)) {
            deletedIds.add(id);
          }
        });

        const filtered: T[] = results.filter((item: T): boolean => !deletedIds.has(item.id));
        return [...overlay, ...filtered];
      };
    },
  },
});
